import { useRef, useState } from "react";
import { toast } from "sonner";

import { useCarrierDispatchCenters, useCreateVehicle } from "@/api/carrier";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { hasHttpStatus } from "@/lib/api-error";

interface CreateVehicleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const TYPE_OPTIONS = [
  "BICYCLE",
  "MOTORCYCLE",
  "CAR",
  "VAN",
  "TRUCK",
  "REFRIGERATED_VAN",
  "REFRIGERATED_TRUCK",
] as const;

/**
 * Register a vehicle — `POST /carrier/vehicles`.
 *
 * There was no create-vehicle screen anywhere in the platform, admin included:
 * a carrier could edit a truck but never add one, so fleets arrived by seed.
 *
 * `code`, `licensePlate` and `vin` are globally unique and cannot be changed
 * afterwards (the edit dialog's allowlist excludes them), which is why they are
 * asked for here and only here.
 */
export function CreateVehicleDialog({
  open,
  onOpenChange,
}: CreateVehicleDialogProps) {
  const create = useCreateVehicle();
  const centers = useCarrierDispatchCenters();
  const submissionInFlight = useRef(false);
  const [submitting, setSubmitting] = useState(false);

  const [dispatchCenterId, setDispatchCenterId] = useState("");
  const [code, setCode] = useState("");
  const [type, setType] = useState<string>("VAN");
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [year, setYear] = useState("");
  const [licensePlate, setLicensePlate] = useState("");
  const [vin, setVin] = useState("");
  const [error, setError] = useState<string | null>(null);

  const centerOptions = centers.data ?? [];
  // A vehicle is parked at a dispatch centre, and the centre is what carries
  // the scope. With no centre there is nowhere to put it.
  const noCenters =
    !centers.isLoading && !centers.error && centerOptions.length === 0;
  const pending = create.isPending || submitting;

  const reset = () => {
    setDispatchCenterId("");
    setCode("");
    setType("VAN");
    setMake("");
    setModel("");
    setYear("");
    setLicensePlate("");
    setVin("");
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submissionInFlight.current) return;
    submissionInFlight.current = true;
    setSubmitting(true);
    setError(null);
    try {
      await create.mutateAsync({
        dispatchCenterId,
        code: code.trim(),
        type,
        make: make.trim(),
        model: model.trim(),
        year: Number.parseInt(year, 10),
        licensePlate: licensePlate.trim(),
        ...(vin.trim() && { vin: vin.trim() }),
      });
      toast.success("Vehicle registered.");
      reset();
      onOpenChange(false);
    } catch (err) {
      const res = (err as { response?: { data?: { error?: string } } })
        ?.response?.data;
      // The server speaks in codes. A carrier should read a sentence.
      setError(
        hasHttpStatus(err, 403)
          ? "You do not have permission to register vehicles."
          : res?.error === "DUPLICATE_VEHICLE"
            ? "A vehicle with this code, plate, or VIN already exists."
            : (res?.error ?? "Could not register the vehicle. Try again.")
      );
    } finally {
      submissionInFlight.current = false;
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && pending) return;
        if (!next) reset();
        onOpenChange(next);
      }}
      title="Add vehicle"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="nv-center" className="block text-sm font-medium mb-1">
            Dispatch center
          </label>
          <NativeSelect
            id="nv-center"
            required
            value={dispatchCenterId}
            onChange={(e) => setDispatchCenterId(e.target.value)}
            disabled={pending || centers.isLoading || noCenters}
          >
            <option value="">Select a center...</option>
            {centerOptions.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </NativeSelect>
          {centers.isLoading && (
            <p role="status" className="mt-1 text-sm text-muted-foreground">
              Loading dispatch centers...
            </p>
          )}
          {centers.error && (
            <div role="alert" className="mt-2 space-y-2">
              <p className="text-sm text-destructive">
                Failed to load dispatch centers.
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm-touch"
                onClick={() => centers.refetch()}
              >
                Retry
              </Button>
            </div>
          )}
          {noCenters && (
            <p className="mt-1 text-sm text-muted-foreground">
              Add a dispatch center first — a vehicle is based at one.
            </p>
          )}
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="nv-code" className="block text-sm font-medium mb-1">
              Code
            </label>
            <Input
              id="nv-code"
              required
              placeholder="VAN-01"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              disabled={pending}
            />
          </div>
          <div>
            <label htmlFor="nv-type" className="block text-sm font-medium mb-1">
              Type
            </label>
            <NativeSelect
              id="nv-type"
              value={type}
              onChange={(e) => setType(e.target.value)}
              disabled={pending}
            >
              {TYPE_OPTIONS.map((t) => (
                <option key={t} value={t}>
                  {t.replace(/_/g, " ")}
                </option>
              ))}
            </NativeSelect>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <label htmlFor="nv-make" className="block text-sm font-medium mb-1">
              Make
            </label>
            <Input
              id="nv-make"
              required
              value={make}
              onChange={(e) => setMake(e.target.value)}
              disabled={pending}
            />
          </div>
          <div>
            <label
              htmlFor="nv-model"
              className="block text-sm font-medium mb-1"
            >
              Model
            </label>
            <Input
              id="nv-model"
              required
              value={model}
              onChange={(e) => setModel(e.target.value)}
              disabled={pending}
            />
          </div>
          <div>
            <label htmlFor="nv-year" className="block text-sm font-medium mb-1">
              Year
            </label>
            <Input
              id="nv-year"
              type="number"
              required
              min={1950}
              max={2100}
              value={year}
              onChange={(e) => setYear(e.target.value)}
              disabled={pending}
            />
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label
              htmlFor="nv-plate"
              className="block text-sm font-medium mb-1"
            >
              License plate
            </label>
            <Input
              id="nv-plate"
              required
              value={licensePlate}
              onChange={(e) => setLicensePlate(e.target.value)}
              disabled={pending}
            />
          </div>
          <div>
            <label htmlFor="nv-vin" className="block text-sm font-medium mb-1">
              VIN (optional)
            </label>
            <Input
              id="nv-vin"
              value={vin}
              onChange={(e) => setVin(e.target.value)}
              disabled={pending}
            />
          </div>
        </div>

        <p className="text-sm text-muted-foreground">
          Code, plate and VIN identify the vehicle and cannot be changed later.
        </p>

        {error && (
          <p
            role="alert"
            aria-label="Vehicle registration failed"
            className="text-sm text-destructive"
          >
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            size="touch"
            disabled={pending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            size="touch"
            disabled={
              pending || centers.isLoading || !!centers.error || noCenters
            }
          >
            {pending ? "Adding..." : "Add vehicle"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
